import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import type { OffsetPageQueryDTO } from "@/shared/contracts/base";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { confirm, toast } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import type { CustomerGroupDTO } from "../api/client";
import {
  useCustomerGroupsQuery,
  useDeleteCustomerGroup,
} from "../api/queries";
import { CustomerGroupFormDialog } from "../ui/CustomerGroupFormDialog";
import { CustomerGroupsTable } from "../ui/CustomerGroupsTable";
import { CustomerGroupsToolbar } from "../ui/CustomerGroupsToolbar";

export function CustomerGroupsPage() {
  const nav = useNavigate({ from: "/app/customers/groups" });
  const search: OffsetPageQueryDTO = useSearch({
    from: "/app/customers/groups",
  });
  const list = useCustomerGroupsQuery({
    offset: search.offset,
    limit: search.limit,
    sort: search.sort,
    filters: search.filters,
  });
  const canManage = useActionPermission([
    "customers:create",
    "customers:update",
    "customers:delete",
  ]);
  const deleteGroup = useDeleteCustomerGroup();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<CustomerGroupDTO | null>(null);

  const handleCreate = () => {
    setEditingGroup(null);
    setDialogOpen(true);
  };

  const handleEdit = (row: CustomerGroupDTO) => {
    setEditingGroup(row);
    setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    const ok = await confirm({
      title: "ລຶບກຸ່ມລູກຄ້າ",
      description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລຶບກຸ່ມນີ້? ລູກຄ້າໃນກຸ່ມຈະຖືກຍ້າຍອອກ",
      actionText: "ລຶບ",
      ActionProps: { variant: "destructive" },
    });
    if (ok) {
      toast.promise(deleteGroup.mutateAsync(id), {
        loading: "ກຳລັງລຶບ...",
        success: "ລຶບສຳເລັດ",
        error: "ລຶບລົ້ມເຫຼວ",
      });
    }
  };

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "ກຸ່ມລູກຄ້າ" },
          ]}
        />
      </Header>
      <Main>
        <CustomerGroupsToolbar onCreate={handleCreate} canCreate={canManage} />
        <div className="flex flex-col rounded-xl border bg-card pt-2">
          <CustomerGroupsTable
            canManage={!!canManage}
            data={list.data?.data ?? []}
            isLoading={list.isLoading}
            offset={search.offset ?? 0}
            limit={search.limit ?? 20}
            totalCount={list.data?.meta?.total ?? 0}
            sortBy={search.sort?.[0]?.field}
            sortOrder={search.sort?.[0]?.dir as "asc" | "desc" | undefined}
            onPaginationChange={(offset, limit) =>
              nav({ search: { ...search, offset, limit } })
            }
            onSortingChange={(id, desc) =>
              nav({
                search: {
                  ...search,
                  sort: [{ field: id, dir: desc ? "desc" : "asc" }],
                },
              })
            }
            onEdit={handleEdit}
            onDelete={handleDelete}
          />
        </div>
        <CustomerGroupFormDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          group={editingGroup}
        />
      </Main>
    </>
  );
}
